import WalletConnectorController, { COPY, isUserRejection, isMobileAdapter, withinWalletAnswerLimit } from "./wallet_connector_controller"
import { ready } from "@solana/connector/headless"
import { toBase58 } from "./base58"
import { createPrivyClient } from "./privy_client"
import {
  transactionMessage, signerIndex, withSignature, firstSignature, sendRawTransaction,
  waitForSignature, bytesFromBase64, bytesToBase64
} from "./solana_tx"

// Plain-language copy for the provisioned-wallet path (R16).
export const PRIVY_COPY = {
  unavailable: "We couldn't reach your wallet just now. Please try again.",
  expired: "Your sign-in expired. Sign in again with your email, then come back."
}

// One transaction, one signature (R6/R20): the server builds it, the wallet
// signs and sends it, the server records the confirmation. The wallet that
// signs has to be the one the plan was set up with — a different account
// connected by mistake is caught here rather than by the server.
//
// Two kinds of wallet sign (KTD5). A session opened by connecting an
// extension wallet signs through @solana/connector, exactly as before. A
// session opened through the email door signs with the provisioned wallet:
// the SDK signs the transaction's message bytes and the signature goes into
// this wallet's slot — the same build/confirm contract either way, and the
// server cannot tell the difference. Approvals, stops and changes sign and
// hand the transaction back for the executor to pay for and send
// (signForServer); one-tap purchases sign and send (signAndSend).
export default class WalletSigningController extends WalletConnectorController {
  static targets = [ "connect", "confirm" ]
  static values = {
    address: String,
    provider: String,
    appId: String,
    sdkUrl: String,
    rpcUrl: String
  }

  // Which extension signed successfully last time, so the next sign can open
  // it directly instead of asking again.
  static rememberedConnectorKey = "sproot-wallet-connector"

  connect() {
    if (this.privyProvider) {
      // The session's wallet needs no picking; the SDK bootstraps on the
      // first sign instead of on page load (KTD1).
      this.connected = true
      this.confirmTargets.forEach((button) => { button.disabled = false })
      return
    }
    super.connect()
    // The sign button is live before a wallet is connected: pressing it
    // connects first (ensureConnected), so one tap does the whole thing.
    this.confirmTargets.forEach((button) => { button.disabled = false })
  }

  disconnect() {
    if (this.privyProvider) {
      if (this.privyTeardown) this.privyTeardown()
      return
    }
    super.disconnect()
  }

  get privyProvider() {
    return this.providerValue === "privy"
  }

  // What the status line says while the wallet is being asked to sign.
  get walletPromptCopy() {
    return this.privyProvider ? "Confirming with your wallet…" : "Check your wallet"
  }

  // The provisioned wallet signs on its own (we are working); an extension
  // or wallet app waits on the saver to approve (the waiting mark).
  get walletPromptState() {
    return this.privyProvider ? "working" : "waiting"
  }

  selectWallet(connectorId) {
    return this.connectWallet(connectorId)
  }

  // Connects without asking when it can — the only wallet on the device, or
  // the one that signed last time. Only when neither applies does the wallet
  // list appear. Resolves true once the plan's wallet is connected.
  async ensureConnected() {
    if (this.connected || this.privyProvider) return true
    try { await ready } catch {}

    const { connectors } = this.client.getSnapshot()
    const usable = connectors.filter((connector) => connector.ready)
    if (usable.length === 0) {
      this.fail(COPY.noWallets)
      return false
    }

    const pick = usable.length === 1
      ? usable[0]
      : usable.find((connector) => connector.id === this.rememberedConnectorId)
    if (!pick) {
      this.revealList("Which wallet does your plan use?")
      return false
    }

    await this.connectWallet(pick.id)
    if (!this.connected) this.revealList()

    // On a phone every wallet request opens the wallet app, and Chrome opens
    // an app only in answer to a tap. The tap that connected is spent, so the
    // signature waits for the next one: the button is live again and says so.
    if (this.connected && isMobileAdapter(this.connector)) {
      this.status(COPY.mobileConnected, "waiting")
      return false
    }
    return this.connected
  }

  async connectWallet(connectorId) {
    this.clearError()
    this.status("Check your wallet", "waiting")

    try {
      const selected = await this.openWallet(connectorId)
      this.account = selected.account
      this.connector = this.client.getConnector(connectorId)

      if (selected.address !== this.addressValue) {
        return this.fail(COPY.wrongWallet)
      }
    } catch (error) {
      this.log("connect failed", error)
      return this.fail(error.rejected ? COPY.rejected : error.userMessage || COPY.connectFailed)
    }

    this.connected = true
    this.rememberedConnectorId = connectorId
    if (this.hasListTarget) this.listTarget.hidden = true
    this.status("Wallet connected — you're ready.", "done")
    // A button whose press is still running (connect on the way to a sign)
    // stays busy until that press finishes.
    this.confirmTargets.forEach((button) => { if (!("busy" in button.dataset)) button.disabled = false })
  }

  revealList(message) {
    if (message) this.status(message, "waiting")
    if (this.hasListTarget) this.listTarget.hidden = false
  }

  get rememberedConnectorId() {
    try {
      return localStorage.getItem(this.constructor.rememberedConnectorKey)
    } catch {
      return null
    }
  }

  set rememberedConnectorId(connectorId) {
    try {
      localStorage.setItem(this.constructor.rememberedConnectorKey, connectorId)
    } catch {}
  }

  async signAndSend(transactionBase64) {
    if (this.privyProvider) return this.privySignAndSend(transactionBase64)

    const bytes = bytesFromBase64(transactionBase64)
    const chain = `solana:${this.clusterValue}`
    const sendFeature = this.connector?.features?.["solana:signAndSendTransaction"]
    const signFeature = this.connector?.features?.["solana:signTransaction"]
    if (!sendFeature && !signFeature) throw new Error("wallet cannot sign transactions")

    if (sendFeature) {
      try {
        const [result] = await withinWalletAnswerLimit(
          sendFeature.signAndSendTransaction({ account: this.account, transaction: bytes, chain })
        )
        return typeof result.signature === "string" ? result.signature : toBase58(result.signature)
      } catch (error) {
        // The investor said no: that stands. So does a wallet that never
        // answered — asking again would open it on a tap that is already
        // spent. Anything else is the extension failing on its own send path
        // (Phantom's "Unexpected error" before it shows a prompt, seen
        // 2026-09-13), and a wallet that can still sign gets a second chance
        // the way the provisioned wallet works: it signs, and the app hands
        // the bytes to the cluster itself.
        if (isUserRejection(error) || error.timedOut || !signFeature || !this.rpcUrlValue) throw error
        this.log("sign-and-send failed, falling back to sign then send", error)
      }
    }

    const [signed] = await withinWalletAnswerLimit(
      signFeature.signTransaction({ account: this.account, transaction: bytes, chain })
    )
    const signedBytes = signed.signedTransaction
    const signature = toBase58(firstSignature(signedBytes))
    await sendRawTransaction(this.rpcUrlValue, bytesToBase64(signedBytes))
    await waitForSignature(this.rpcUrlValue, signature)
    return signature
  }

  // Sign only (2026-10-17): the executor pays the fee, so the investor's
  // signature goes into their slot and the whole transaction goes back to
  // the server, which has the executor co-sign and send it. Resolves to the
  // partially signed transaction, base64. Both wallet kinds end up here for
  // approvals, stops and changes; one-tap purchases keep signAndSend.
  async signForServer(transactionBase64) {
    if (this.privyProvider) return this.privySign(transactionBase64)

    const bytes = bytesFromBase64(transactionBase64)
    const chain = `solana:${this.clusterValue}`
    const signFeature = this.connector?.features?.["solana:signTransaction"]
    if (!signFeature) throw new Error("wallet cannot sign transactions")

    const [signed] = await withinWalletAnswerLimit(
      signFeature.signTransaction({ account: this.account, transaction: bytes, chain })
    )
    return bytesToBase64(signed.signedTransaction)
  }

  // The provisioned wallet signs the message bytes and the signature is
  // written into this wallet's slot — whichever slot that is; since
  // 2026-10-17 the fee payer's slot belongs to the executor.
  async privySign(transactionBase64) {
    const { sdk, privy } = await this.privyClient()
    const account = await this.privyWalletAccount(sdk, privy)
    const provider = await privy.embeddedWallet.getSolanaProvider(account)

    const transactionBytes = bytesFromBase64(transactionBase64)
    const messageBytes = transactionMessage(transactionBytes)
    const slot = signerIndex(messageBytes, this.addressValue)
    if (slot < 0) throw new Error("session wallet is not a signer on this transaction")

    const { signature } = await provider.request({
      method: "signMessage",
      params: { message: bytesToBase64(messageBytes) }
    })
    return bytesToBase64(withSignature(transactionBytes, slot, bytesFromBase64(signature)))
  }

  // A one-tap purchase with the provisioned wallet: it is the fee payer
  // there, so the signature it writes is slot 0's, and the browser submits
  // the result over RPC (the SDK's own sign-and-send does the same).
  async privySignAndSend(transactionBase64) {
    const signed = await this.privySign(transactionBase64)
    const { sdk } = await this.privyClient()
    const send = sdk.sendRawTransaction || sendRawTransaction
    await send(this.rpcUrlValue, signed)
    const sent = toBase58(firstSignature(bytesFromBase64(signed)))
    await waitForSignature(this.rpcUrlValue, sent)
    return sent
  }

  async privyClient() {
    try {
      this.privyClientPromise ||= createPrivyClient({
        sdkUrl: this.sdkUrlValue,
        appId: this.appIdValue,
        container: this.element
      }).then((client) => {
        this.privyTeardown = client.teardown
        return client
      })
      return await this.privyClientPromise
    } catch (error) {
      this.privyClientPromise = null
      this.log("privy bootstrap failed", error)
      throw this.userError(PRIVY_COPY.unavailable, error)
    }
  }

  // The session's embedded wallet, which must be the one the plan uses.
  async privyWalletAccount(sdk, privy) {
    let user
    try {
      ({ user } = await privy.user.get())
    } catch (error) {
      this.log("privy session lookup failed", error)
      throw this.userError(PRIVY_COPY.expired, error)
    }
    const account = sdk.getUserEmbeddedSolanaWallet(user)
    if (!account) throw this.userError(PRIVY_COPY.expired)
    if (account.address !== this.addressValue) throw this.userError(COPY.wrongWallet)
    return account
  }

  userError(copy, cause) {
    const error = new Error(cause?.message || copy)
    error.userMessage = copy
    return error
  }
}
